import React, { Dispatch, SetStateAction, useContext, useState, useEffect } from "react";
import Typography from "@mui/material/Typography";
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";
import DownloadIcon from "@mui/icons-material/Download";
import CloseIcon from "@mui/icons-material/Close";
import InsightReportViewModel from "./ViewModels/InsightReportViewModel";
import Box from "@mui/material/Box";
import { MsalContext } from "../Contexts/MsalContext"
import { APIContext } from "../Contexts/APIContext"
import Dialog from "@mui/material/Dialog";
import { InsightView } from "../ViewModels/InsightView";

interface IInsightResponseDialogProps {
    client: string
    view: InsightView
    insightReportId: number
    setInsightReportId: Dispatch<SetStateAction<number | null>>
}

export default function InsightReportDialog({ client, view, insightReportId, setInsightReportId }: IInsightResponseDialogProps) {
    const { msalFetch } = useContext(MsalContext);
    const { get, showMessage } = useContext(APIContext);
    const [insightReport, setInsightReport] = useState<InsightReportViewModel | null>(null)

    useEffect(() => {
        let controllerAction: string;
        switch (view) {
            case InsightView.Agent:
                controllerAction = 'GetAgentReport';
                break;
            case InsightView.Customer:
                controllerAction = 'GetCustomerReport';
                break;
            case InsightView.Operational:
                controllerAction = 'GetOperationalReport';
                break;
            default:
                throw new Error('Unknown InsightView');
        }
        get('Insight', `${controllerAction}?id=${insightReportId}`, (data) => setInsightReport(data))
    }, [])

    const getPdf = () => {
        const reportDate = insightReport!.timePeriod === "Day"
            ? insightReport!.startDate
            : insightReport!.startDate + "-" + insightReport!.reportDate

        let controllerAction: string;
        switch (view) {
            case InsightView.Agent:
                controllerAction = 'CreateAgentInsightReport';
                break;
            case InsightView.Customer:
                controllerAction = 'CreateCustomerInsightReport';
                break;
            case InsightView.Operational:
                controllerAction = 'CreateOperationalInsightReport';
                break;
            default:
                throw new Error('Unknown InsightView');
        }

        msalFetch(`api/PDFGenerator/${controllerAction}?id=${insightReportId}`)
            .then(response => {
                if (response.status === 200) {
                    return response.blob().then(data => {
                        var url = window.URL.createObjectURL(data);
                        var a = document.createElement('a');
                        a.href = url;
                        a.download = client + "_" + insightReport!.channel + "_" + insightReport!.insightName + "_" + reportDate + ".pdf";
                        document.body.appendChild(a);
                        a.click();
                        a.remove();
                    })
                }
                else {
                    response.text().then(text => showMessage("error", text))
                    return;
                }
            })
    }

    // Exports the report body as a standalone HTML file, used when the report's ReportExportFormat is HTML
    const getHtml = () => {
        const escape = (text?: string | null) => (text ?? '')
            .replace(/&/g, '&amp;')
            .replace(/</g, '&lt;')
            .replace(/>/g, '&gt;')
            .replace(/"/g, '&quot;');

        const reportDate = insightReport!.timePeriod === "Day"
            ? insightReport!.startDate
            : insightReport!.startDate + "-" + insightReport!.reportDate

        const title = `${insightReport!.questionCategory} - ${insightReport!.channel}`;
        const html = `<!DOCTYPE html>
<html lang="en">
<head>
<meta charset="utf-8">
<meta name="viewport" content="width=device-width, initial-scale=1">
<base target="_blank">
<title>${escape(title)}</title>
<style>
    body { font-family: Arial, Helvetica, sans-serif; margin: 0; color: #222; }
    main { padding: 8px 24px; font-size: 16px; line-height: 1.5; }
</style>
</head>
<body>
<main>
${insightReport!.response}
</main>
</body>
</html>`;

        const url = window.URL.createObjectURL(new Blob([html], { type: 'text/html' }));
        const a = document.createElement('a');
        a.href = url;
        a.download = client + "_" + insightReport!.channel + "_" + insightReport!.insightName + "_" + reportDate + ".html";
        document.body.appendChild(a);
        a.click();
        a.remove();
        window.URL.revokeObjectURL(url);
    }

    const scrollbarSx = {
        overflowY: 'auto',
        '::-webkit-scrollbar': {
            width: '6px',
            height: '6px',
        },
        '::-webkit-scrollbar-track': {
            background: 'transparent',
        },
        '::-webkit-scrollbar-thumb': {
            backgroundColor: 'grey',
            borderRadius: '3px',
            border: 'none',
        },
    }

    const reportBody = insightReport &&
        <div dangerouslySetInnerHTML={{ __html: insightReport.response }} style={{ padding: '8px 40px', fontSize: '16px' }} />

    const reportDates = insightReport &&
        (insightReport.timePeriod !== 'Day'
            ? `${insightReport.startDate} - ${insightReport.endDate}`
            : insightReport.startDate)

    return (
        <>
            {
                insightReport &&
                <Dialog
                    open={insightReportId !== null}
                    fullScreen
                    PaperProps={
                        {
                            sx: {
                                padding: 0
                            }
                        }
                    } >
                    <Box sx={{ height: '100vh', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
                        <Box sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 3,
                            px: '40px',
                            py: '6px',
                            borderBottom: 'solid 1px black',
                            flexShrink: 0
                        }}>
                            <img
                                src={`https://agentdashboardblob.blob.core.windows.net/$web/Firestone/${client}.png`}
                                style={{ maxHeight: "40px", maxWidth: "10vw" }}
                            />
                            <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 3, flexGrow: 1, flexWrap: 'wrap', minWidth: 0 }}>
                                <Typography variant='body1'>{insightReport.channel}</Typography>
                                <Typography variant='body1'>
                                    {(view === InsightView.Agent || view === InsightView.Operational)
                                        ? insightReport.template
                                        : insightReport.questionCategory}
                                </Typography>
                                {view === InsightView.Agent && <Typography variant='body1'>{insightReport.agentName}</Typography>}
                                <Typography variant='body1'>{insightReport.timePeriod}</Typography>
                                <Typography variant='body1'>{reportDates}</Typography>
                            </Box>
                            <Box sx={{ display: 'flex', gap: 1, flexShrink: 0 }}>
                                <Tooltip title='Export'>
                                    <IconButton aria-label='Export' onClick={() => insightReport.reportExportFormat?.trim().toUpperCase() === 'HTML' ? getHtml() : getPdf()}>
                                        <DownloadIcon />
                                    </IconButton>
                                </Tooltip>
                                <Tooltip title='Close'>
                                    <IconButton aria-label='Close' onClick={() => setInsightReportId(null)}>
                                        <CloseIcon />
                                    </IconButton>
                                </Tooltip>
                            </Box>
                        </Box>
                        <Box sx={{ flexGrow: 1, ...scrollbarSx }}>
                            {reportBody}
                        </Box>
                    </Box>
                </Dialog>
            }
        </>
    );
};
